import { ApolloError, useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { ReactElement, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import {
  addRoutingProjectPlanRouteStop,
  getOptimizationByDow,
  updateRoutesPolyline
} from 'features/RoutingProjects/models'
import {
  ADD_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS,
  CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_STOPS
} from 'features/RoutingProjects/mutations'
import {
  IAddStopFormValues,
  IRoutingProjectPlanModel,
  IRoutingProjectPlanOptimization
} from 'features/RoutingProjects/types'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { Input } from 'components/Input'
import { useMap } from '@vis.gl/react-google-maps'
import { adjustMapBoundsByRoutes, getAvailableDowsByRoutesAndLocations } from 'features/RoutingProjects/utils'
import { SelectDow } from 'features/RoutingProjects/components/SelectDow'
import {
  ChangeRoutingProjectPlanOptimizationRouteStopsPlanOptimizationInput,
  RoutingProjectDow
} from 'gql/generated/graphql'

const messages = defineMessages({
  labelPlaceholder: {
    id: 'routingProjectSidebar.optimizedRoutesList.addStop.labelPlaceholder',
    defaultMessage: 'Enter a name'
  },
  coordinatePlacholder: {
    id: 'routingProjectSidebar.optimizedRoutesList.addStop.coordinatePlacholder',
    defaultMessage: 'E.g. 52.5200, 13.4050'
  }
})

interface IAddStopModalProps {
  routingProjectId: string
  planModel: IRoutingProjectPlanModel
  currentOptimization: IRoutingProjectPlanOptimization
  routeIndex: number
  setPlanModel: (planModel: IRoutingProjectPlanModel) => void
  closeModal: () => void
}

export const AddStopModal = ({
  routingProjectId,
  planModel,
  currentOptimization,
  routeIndex,
  setPlanModel,
  closeModal
}: IAddStopModalProps): ReactElement => {
  const intl = useIntl()
  const map = useMap()
  const [error, setError] = useState<ApolloError | null>(null)
  const [addRoutingProjectPlanDatasetLocations] = useMutation(ADD_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS)
  const [changeRoutingProjectPlanRouteStops] = useMutation(CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_STOPS, {
    onError() {
      // Do nothing
    }
  })
  const selectedRoute = currentOptimization.routes[routeIndex]
  const availableDows = getAvailableDowsByRoutesAndLocations({
    routes: [selectedRoute],
    optimizations: planModel.optimizations,
    locations: [],
    shouldFilterLockedRoutes: true
  })
  const initialValues: IAddStopFormValues = {
    label: '',
    coordinates: '',
    dows: availableDows
  }

  const getOptimizationsInputByDow = (
    dows: RoutingProjectDow[],
    newPlanModel: IRoutingProjectPlanModel,
    newRouteGroupId: string | undefined
  ): ChangeRoutingProjectPlanOptimizationRouteStopsPlanOptimizationInput[] => {
    const optimizations: ChangeRoutingProjectPlanOptimizationRouteStopsPlanOptimizationInput[] = []
    for (const dow of dows) {
      const optimization = getOptimizationByDow(newPlanModel.optimizations, dow)
      const updatedRoute = optimization?.routes.find((route) => route.groupId === newRouteGroupId)
      if (updatedRoute) {
        optimizations.push({
          id: optimization?.id,
          route: {
            id: updatedRoute.id,
            polyline: updatedRoute.polyline,
            distance: updatedRoute.distance,
            duration: updatedRoute.duration,
            stops: updatedRoute.stops.map((stop) => {
              return {
                locationID: stop.location.id,
                departure: stop.departure,
                arrival: stop.arrival
              }
            })
          }
        })
      }
    }
    return optimizations
  }

  const handleSubmit = async (
    values: IAddStopFormValues,
    { setSubmitting }: FormikHelpers<IAddStopFormValues>
  ): Promise<void> => {
    try {
      const [latitude, longitude] = values.coordinates
        .split(',')
        .map((coordinate): number => parseFloat(coordinate.trim()))
      const addDatasetLocationsResponse = await addRoutingProjectPlanDatasetLocations({
        variables: {
          input: {
            id: routingProjectId,
            plan: {
              id: planModel.id,
              dataset: {
                locations: [
                  {
                    label: values.label,
                    dow: values.dows,
                    coordinates: {
                      latitude: latitude.toString(),
                      longitude: longitude.toString()
                    }
                  }
                ]
              }
            }
          }
        }
      })
      const locationAdded = addDatasetLocationsResponse.data?.addRoutingProjectPlanDatasetLocations?.locations.at(0)
      if (!locationAdded?.id) {
        throw new Error('Failed to add location')
      }
      const newPlan = addRoutingProjectPlanRouteStop({
        plan: planModel,
        optimizationId: currentOptimization.id,
        routeIndex,
        location: {
          id: locationAdded.id,
          label: values.label,
          coordinates: {
            latitude,
            longitude
          },
          dows: values.dows
        },
        dows: values.dows
      })
      let newPlanModel = {
        ...planModel,
        ...newPlan
      }
      const newRouteGroupId = newPlanModel.optimizations.find(
        (optimization) => optimization.id === currentOptimization.id
      )?.routes[routeIndex].groupId

      for (const dow of values.dows) {
        const optimization = getOptimizationByDow(newPlanModel.optimizations, dow)
        if (optimization) {
          const updatedRouteIndex = optimization.routes.findIndex((route) => route.groupId === newRouteGroupId)

          if (updatedRouteIndex !== undefined && updatedRouteIndex >= 0) {
            const planModelWithNewRoutesPolyline = await updateRoutesPolyline({
              planModel: newPlanModel,
              optimizationId: optimization.id,
              updatedRoutesIndex: [updatedRouteIndex]
            })
            newPlanModel = {
              ...newPlanModel,
              ...planModelWithNewRoutesPolyline
            }
          }
        }
      }
      setPlanModel(newPlanModel)
      if (map) {
        setTimeout(() => {
          const newCurrentOptimization = newPlanModel.optimizations.find(
            (optimization) => optimization.id === currentOptimization.id
          )
          adjustMapBoundsByRoutes({
            map,
            routes: newCurrentOptimization?.routes[routeIndex] ? [newCurrentOptimization?.routes[routeIndex]] : []
          })
        }, 1)
      }

      const optimizationsInput = getOptimizationsInputByDow(values.dows, newPlanModel, newRouteGroupId)
      await changeRoutingProjectPlanRouteStops({
        variables: {
          input: {
            id: routingProjectId,
            plan: {
              id: newPlanModel.id,
              optimizations: optimizationsInput
            }
          }
        }
      })
      closeModal()
    } catch (ex) {
      // @ts-expect-error FIXME
      setError(ex)
    }
    setSubmitting(false)
  }

  const handleCancel = (): void => {
    closeModal()
  }

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage id="routingProjectSidebar.optimizedRoutesList.addStop.title" defaultMessage="Add stop" />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { handleChange, setFieldValue, handleBlur, handleSubmit, touched, errors, values, isSubmitting } = props

          const handleDowsChange = (selectedDows: RoutingProjectDow[]): void => {
            setFieldValue('dows', selectedDows)
          }

          return (
            <form onSubmit={handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <Input
                      id="label"
                      value={values.label}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.label}
                      errors={errors.label}
                      label={<FormattedMessage id="routingProjects.create.name.label" defaultMessage="Name" />}
                      placeholder={intl.formatMessage(messages.labelPlaceholder)}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Input
                      id="coordinates"
                      label={
                        <FormattedMessage id="routingProjects.create.coordinates.label" defaultMessage="Coordinates" />
                      }
                      placeholder={intl.formatMessage(messages.coordinatePlacholder)}
                      value={values.coordinates}
                      onChange={handleChange}
                      errors={errors.coordinates}
                      touched={touched.coordinates}
                    />
                  </Column>
                </Row>
                {planModel.dataset?.hasDow && (
                  <Row>
                    <Column>
                      <SelectDow
                        id="dows"
                        label={<FormattedMessage id="routingProjects.create.dow.label" defaultMessage="DOW" />}
                        placeholder={intl.formatMessage({
                          id: 'routingProjects.create.dow.placeholder',
                          defaultMessage: 'Select days of the week'
                        })}
                        values={values.dows}
                        readOnlyValues={[currentOptimization.dow]}
                        onChange={handleDowsChange}
                        errors={errors.dows}
                        touched={touched.dows}
                        availableDows={availableDows}
                        onBlur={handleBlur}
                      />
                    </Column>
                  </Row>
                )}
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="routingProjectSidebar.optimizedRoutesList.addStop.error"
                        defaultMessage="There was an error adding the stop"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button kind="cancel" onClick={handleCancel}>
                    <FormattedMessage id="modal.actions.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button kind="primary" loading={isSubmitting} type="submit">
                    <FormattedMessage
                      id="routingProjectSidebar.optimizedRoutesList.addStop.action"
                      defaultMessage="Create"
                    />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
